import { createAsyncThunk, createSlice, PayloadAction, ThunkDispatch } from '@reduxjs/toolkit';
import {
  IBooking,
  BookingType,
  StatusUnconfirmed,
  IBookingStatus,
  ITableItem,
  AdminServiceLog,
  BookingStatusNames,
  IBookingTime,
  BookingMethods,
  JOIN_PREFIX,
  FadeMode,
  ToastType,
} from 'shared-types/index';
import { BookingCreatorState } from 'booking-creator/rootReducer';
import { DiaryRootState } from 'app/appRoot/rootReducer';
import { from, Observable, of, throwError } from 'rxjs';
import {
  first,
  switchMap,
  map,
  catchError,
  timeout,
  tap,
  retry,
  mergeMap,
  retryWhen,
  delay,
} from 'rxjs/operators';
import { StatusService, StatusServiceInstance } from 'external-services/status-service';
import StandbyListService from 'external-services/standby-list-service';
import BookingLookupService from 'external-services/booking-lookup-service';
import TableSelectionService from 'external-services/table-selection-service';
import {
  displayActions,
  updateActiveBookingStatus,
  updateActiveBookingFromCouch,
} from '../display/slice';
import AdminService from 'external-services/diary-admin-service/admin.service';
import SectionsService from 'external-services/sections-service/index';
import { rootDisplayActions } from 'app/reducers/display/slice';
import { SlidingStatesEnum } from 'app/reducers/display/types';
import {
  preUpdate,
  saveEditBooking,
  validationActions,
} from 'booking-creator/reducers/validation/slice';
import { BookingMode } from 'booking-creator/reducers/display/types';
import DateUtilsService from 'external-services/date-utils-service';
import { updateStandByTableSeaterOnTimeChange } from 'booking-creator/reducers/standbyList/helper';
import { cloneDeep } from 'lodash-es';
import { IConfirmStandByResponse, IStandbyListState, State } from './types';
import { SaveStatus } from 'booking-creator/reducers/validation/types';
import { resetReducers } from 'booking-creator/hooks';
import { AppDispatch } from 'appRoot/Store';
import { errorStateActions, getAccordionDetail } from 'app/reducers/errorState/slice';
import { bookingActions } from 'app/reducers/booking/slice';
import smsService from 'external-services/sms-service';
import { CONTACT_MSG, PROBLEMS_MSG, REFRESH_MSG } from 'appRoot/app-error-messages';
import { transitionActions } from 'booking-creator/reducers/transition/slice';
import StaffService from 'external-services/staff-service/staff.service';
import { updateCanSaveEditBooking, checkIfIsStaffEditRequired } from 'booking-creator/reducers/validation/slice'

const NS = 'customerSlice';

const initialState: IStandbyListState = {
  tables: [],
  isManualJoin: false,
  isLoading: false,
  errorMsg: null,
  statusName: null,
  statusOptions: [],
  hasPriceChanges: false,
  isSelectionValid: false,
  isOffline: false,
  tableSelectionList: [],
  tableSelectionListWithJoins: [],
  sectionsLookup: {},
  preselectedTableIds: [],
  state: State.LOADING,
  selectedTableIds: [],
};

const confirmStandby = (booking: IBooking): Observable<IConfirmStandByResponse> => {
  // test different responses
  // return of(403);
  // return of(404);
  // return of(400);
  // return of(408);
  // return of(500);

  const TOO_MANY_CODE = 429;
  const TIMEOUT_CODE = 408;
  let timeoutVar = 10000;
  let retryCount = 0;
  return StandbyListService.upgradeStandbyList(booking).pipe(
    tap(() => {
      retryCount += 1;
      if (retryCount > 1) {
        timeoutVar = 20000;
      }
    }),
    timeout(timeoutVar), // give back end 10 seconds to respond before timing out, change to 20 if failed
    switchMap((status: number) => {
      // waits for back end change to refresh in couch
      return waitForBookingUpdate(booking._id, booking._rev, booking.time).pipe(
        map((booking: IBooking) => ({ status, booking }))
      );
    }),
    /**
     * retry a few times. Sometimes it takes awhile for couch to sync with pouch
     */
    retry(5),
    catchError((err) => {
      if (err === TOO_MANY_CODE) {
        return of({ status: TOO_MANY_CODE, booking: null }); // too many requests
      }
      return of({ status: TIMEOUT_CODE, booking: null }); // timeout
    })
  );
};

/**
 * Waits for pouch to trigger an update on the specified booking. This is useful if booking is expected to changed
 * on the back end and you need to wait for that change to refresh to avoid a couch conflict.
 */
function waitForBookingUpdate(
  bookingId: string,
  bookingRev: string,
  bookingTime: Date
): Observable<IBooking> {
  const CONFLICT_CODE = 409;
  return from(BookingLookupService.getById(bookingId, bookingTime) as Promise<IBooking>).pipe(
    first(),
    mergeMap((booking) => (booking._rev === bookingRev ? throwError(CONFLICT_CODE) : of(booking))),
    retryWhen((errors) =>
      errors.pipe(
        delay(1000), // allow some time between retries
        tap((val) => {
          if (val === CONFLICT_CODE) {
            console.warn(NS, `Same '_rev' detected. Ignoring and retrying.`);
            throw CONFLICT_CODE;
          }
        })
      )
    )
  );
}

export const handleResendClick =
  () =>
    async (dispatch: ThunkDispatch<DiaryRootState, {}, any>, getState: () => DiaryRootState) => {
      const setErrorMsg = (err: string) => dispatch(actions.setErrorMsg(err));
      const setIsLoading = (isLoading: boolean) => dispatch(actions.setLoading(isLoading));
      const state = getState();
      const {
        settingReducer: { generalSettings },
        displayReducer: { activeBooking },
        validationReducer: { staffValue },
      } = state
      const isStaffEditRequired = checkIfIsStaffEditRequired(state);
      const notAllowedToSend = isStaffEditRequired && !staffValue;

      if (notAllowedToSend) {
        dispatch(onStandbyUpdate({ doUpdate: false, isBlocked: true }));
        dispatch(validationActions.setSaveAttempted(true));
        return;
      }
      dispatch(onStandbyUpdate({ doUpdate: true }));
      setErrorMsg(null);
      setIsLoading(true);
      confirmStandby(activeBooking as IBooking).subscribe(
        ({ status, booking }: IConfirmStandByResponse) => {
          setIsLoading(false);

          switch (status) {
            case 200:
              dispatch(onStandbyUpdate({ doUpdate: false, savedBooking: Boolean(booking) }));
              dispatch(rootDisplayActions.setSlideState(SlidingStatesEnum.BCSlidingOut));
              setTimeout(() => {
                dispatch(rootDisplayActions.setSlideState(SlidingStatesEnum.SlideOutComplete));
              }, 300);
              const fullName = [activeBooking.firstName, activeBooking.lastName].filter(Boolean).join(' ');

              dispatch(
                rootDisplayActions.showToastClean({
                  message: `Standby request was successfully resent for ${fullName}.`,
                  type: ToastType.info,
                  handleClose: () => {
                    dispatch(rootDisplayActions.clearToast());
                  }
                })
              );
              setTimeout(() => {
                dispatch(rootDisplayActions.clearToast());
              }, 5000);
              break;

            case 408:
              setErrorMsg(
                'Sorry, server took too long to respond (timeout). Try closing and reopening the booking.'
              );
              dispatch(onStandbyUpdate({ doUpdate: false }));
              break;

            default:
              console.warn(NS, 'confirmStandby failed', status);
              setErrorMsg(StandbyListService.getGenericErrorResponseMessaging(status));
              dispatch(onStandbyUpdate({ doUpdate: false }));
          }
        },
        (err: string) => {
          console.warn(NS, 'confirmStandby failed', err);
          setIsLoading(false);
          dispatch(onStandbyUpdate({ doUpdate: false }));
          setErrorMsg('Sorry, there seems to have been a problem with your internet connection.');
          dispatch(
            errorStateActions.setErrorState({
              errorTitle: 'Confirm standby failed',
              errorMessage: `Sorry, there seems to have been a problem with your internet connection.`,
              accordionDetail: getAccordionDetail(err),
            })
          );
        }
      );
    };

interface IStandbyUpdate {
  doUpdate: boolean;
  savedBooking?: boolean;
  isBlocked?: boolean;
  statusNameForOfflineMode?: boolean;
}

export const onStandbyUpdate = (payload: IStandbyUpdate) => async (dispatch: AppDispatch) => {
  const {
    doUpdate,
    savedBooking = false,
    isBlocked = false,
    statusNameForOfflineMode = false,
  } = payload;
  if (isBlocked) {
    // todo: dispatch an action to Redux - maybe don't need to do anything actually
    // vm.bookingDetails.staffName.$setTouched();
    return false;
  }

  if (statusNameForOfflineMode) {
    // TODO
    //   update(statusNameForOfflineMode)
    //     .then(() => closeDialog());
    console.warn(NS, 'onStandbyUpdate', 'Offline mode not properly handled yet!');
    return true;
  }

  if (doUpdate) {
    const success = await dispatch(preUpdate()); // needs to run preUpdate checks so the booking object gets latest updates
    if (!success) return false; // if validation fails, stop loading

    // vm.state = vm.LOADING;
    dispatch(actions.setLoading(true));
  } else if (savedBooking) {
    // updates back end and waits for couch to refresh
    /**
     * This will have saved the booking on the back end, so we don't need (and don't want) to save it again, as
     * `StandByConfirmationExpiry` would get overridden.
     */
    // closeDialog();
    // comment out bellow as Teryn wants back to viewBooking when clicking seat and send confirmation button
    // dispatch(rootDisplayActions.setSlideState(SlidingStatesEnum.BCSlidingOut));
    setTimeout(() => {
      dispatch(updateActiveBookingFromCouch());
      dispatch(actions.setLoading(false));
      // comment out bellow as Teryn wants back to viewBooking when clicking seat and send confirmation button
      // dispatch(rootDisplayActions.setSlideState(SlidingStatesEnum.SlideOutComplete));
      // resetReducers(dispatch);
    }, 300);
  } else {
    // vm.state = vm.ENTER;
    dispatch(actions.setLoading(false));
  }
  return true;
};

/**
 * If time has been picked from list, `selectedIndex` will be more than -1 and `time` will be null,
 * but if it is a custom time the `time` will be populated.
 */
export const onTimeChanged =
  (payload: { selectedIndex: number; time: string }) =>
    async (dispatch: ThunkDispatch<DiaryRootState, {}, any>, getState: () => DiaryRootState) => {
      const { selectedIndex } = payload;
      const {
        bookingTimesReducer: { availableTimes },
        displayReducer: { activeBooking },
      } = getState() as DiaryRootState;

      if (payload.time) {
        const timeVal = DateUtilsService.setTimeFromString(activeBooking.time, payload.time);
        dispatch(
          displayActions.updateActiveBooking({
            time: timeVal,
          })
        );
      } else {
        dispatch(
          displayActions.updateActiveBooking({
            time: availableTimes[selectedIndex].time,
          })
        );
      }
      await dispatch(updateStandByTableSeaterOnTimeChange());
    };

export const handleSelectedChanged =
  (payload: { selectedTableIds: string[]; tables: ITableItem[]; isManualJoin: boolean }) =>
    async (dispatch: ThunkDispatch<DiaryRootState, {}, any>) => {
      const { selectedTableIds, tables, isManualJoin } = payload;
      dispatch(
        actions.updateStandby({
          isSelectionValid: !!selectedTableIds.length,
          isManualJoin,
          preselectedTableIds: selectedTableIds,
          selectedTableIds,
        })
      );

      dispatch(
        displayActions.updateActiveBooking({
          tables: tables.filter(({ _id }) => selectedTableIds.includes(_id)),
        })
      );
    };

export const handleSendClick =
  () => (dispatch: ThunkDispatch<DiaryRootState, {}, any>, getState: () => DiaryRootState) => {
    const setErrorMsg = (err: string) => dispatch(actions.setErrorMsg(err));
    const setIsLoading = (isLoading: boolean) => dispatch(actions.setLoading(isLoading));
    let tables = null;
    const state = getState() as DiaryRootState;
    const {
      displayReducer: { activeBooking },
      standbyReducer: { preselectedTableIds },
      validationReducer: { staffValue },
    } = state

    const isStaffEditRequired = checkIfIsStaffEditRequired(state);

    const notAllowedToSend = isStaffEditRequired && !staffValue;
    if (notAllowedToSend) {
      dispatch(onStandbyUpdate({ doUpdate: false, isBlocked: true }));
      dispatch(validationActions.setSaveAttempted(true));
      return;
    }
    const passesValidation = dispatch(onStandbyUpdate({ doUpdate: true })); // starts loading status and updates booking object
    if (!passesValidation) {
      return;
    }

    tables = cloneDeep(activeBooking.tables);

    setErrorMsg(null);
    setIsLoading(true);

    // using the first selectedTableId because if using the layout-based table join, which is prefixed with 'join_', there can only be one selected.
    const firstSelectedTableId = preselectedTableIds[0];
    if (firstSelectedTableId && firstSelectedTableId.indexOf(JOIN_PREFIX) === 0) {
      const isManualJoin = false;
      tables = convertToManualJoin(preselectedTableIds, activeBooking.tables, isManualJoin);
      dispatch(
        displayActions.updateActiveBooking({
          tables,
        })
      );
    }

    // adding server logging to capture evidence when tables = null - NBI-3868
    // the condition [tables: null] triggers an exception on the backend side that affects the Widget loading
    // However, bookings with BookingType.StandbyList or BookingType.WaitList are able to save with empty tables (BUT NOT NULL).
    //
    // tables: []    => EXCEPTION FREE
    // tables: null  => TRIGGERS EXCEPTION ON API
    if (!tables) {
      console.log('WARNING - DETECTED NULL TABLES ON BOOKING - UPDATE SBL');
      const notes = `Booking Data: {"_id": "${activeBooking._id}",
                  "_rev": "${activeBooking._rev}", "bookingType": "${activeBooking.bookingType}", "tables": "${tables}"}`;
      try {
        AdminService.serverLoggingDbEvents(
          AdminService.getVenue().id.toString(),
          'Updated Booking with NULL tables. - SBL',
          AdminServiceLog.ERRORLOG,
          notes
        );
      } catch (e) {
        console.warn(NS, 'AdminService.serverLoggingDbEvents error', e);
        dispatch(
          errorStateActions.setErrorState({
            errorTitle: 'Server logging error',
            errorMessage: `There was an error while logging DB events in the server. \n\n${PROBLEMS_MSG} ${REFRESH_MSG} \n\n${CONTACT_MSG}`,
            accordionDetail: getAccordionDetail(e),
          })
        );
      }
    }

    // if (isOffline) {
    //   setIsLoading(false);
    //   booking.status = StatusStandbyPending;
    //   onStandbyUpdate(false, booking, false, BookingStatusNames.StandbyPending);
    //   return;
    // }

    const UpdatedActiveBooking = getState().displayReducer.activeBooking;
    dispatch(bookingActions.setRecentChangeBookingIdToIgnore(UpdatedActiveBooking._id));
    confirmStandby(UpdatedActiveBooking as IBooking).subscribe(
      ({ status }: IConfirmStandByResponse) => {
        switch (status) {
          case 200:
            dispatch(onStandbyUpdate({ doUpdate: false, savedBooking: true }));
            dispatch(displayActions.setMode(BookingMode.viewBooking));
            /**
             * Below part is to fix `Resend`-staff quickly
             * should be analyze more later
             */
            dispatch(validationActions.setStaffValue(''));
            dispatch(transitionActions.setEditBookingFadeMode(FadeMode.fromEditToView));
            break;

          case 408:
            setErrorMsg(
              'Sorry, server took too long to respond (timeout). Try closing and reopening the booking.'
            );
            dispatch(onStandbyUpdate({ doUpdate: false }));
            break;

          default:
            console.warn(NS, 'confirmStandby failed', status);
            setErrorMsg(StandbyListService.getGenericErrorResponseMessaging(status));
            dispatch(onStandbyUpdate({ doUpdate: false }));
        }
      },
      (err: string) => {
        console.warn(NS, 'confirmStandby failed', err);
        setIsLoading(false);
        dispatch(onStandbyUpdate({ doUpdate: false }));
        setErrorMsg('Sorry, there seems to have been a problem with your internet connection.');
      }
    );
  };

const convertToManualJoin = (
  selectedTableIds: string[],
  tableList: ITableItem[],
  isManualJoin: boolean
) => {
  // using the first selectedTableId because if using the layout-based table join, which is prefixed with 'join_', there can only be one selected.
  const firstSelectedTableId = selectedTableIds[0];
  if (!isManualJoin && firstSelectedTableId && firstSelectedTableId.indexOf(JOIN_PREFIX) === 0) {
    // if table join is selected (layout level join), converts it to a manual join
    const selectedTableJoin = tableList.find(({ _id }) => firstSelectedTableId === _id);
    return selectedTableJoin.tablesList;
  } else {
    return tableList.filter(({ _id }) => selectedTableIds.includes(_id));
  }
};

export const handleStandbyReset = createAsyncThunk(
  'standbyList/onStandbyReset',
  async (_, { dispatch, getState }) => {
    const {
      displayReducer: { activeBooking },
      standbyReducer: { statusName },
    } = getState() as BookingCreatorState;
    const booking = cloneDeep(activeBooking); // gets modified, so we need to clone it
    const { isSBPaymentPending, isSBPaymentUnconfirmed } =
      StatusService.getStandbyPaymentPendingState(
        booking.status.statusType,
        booking.paymentDetails,
        booking.bookingType
      );

    const hasPaidMoney = booking.paymentSummary && booking.paymentSummary.amountPaid > 0;
    if (!hasPaidMoney && (isSBPaymentPending || isSBPaymentUnconfirmed)) {
      const statusName = StatusServiceInstance.updateBookingStatusWithPaymentPendingUnconfirmed(
        booking as IBooking
      );
      dispatch(actions.setStatusName(statusName));
    } else {
      booking.status = StatusUnconfirmed;
      dispatch(actions.setStatusName(BookingStatusNames.Unconfirmed));
    }
    dispatch(displayActions.setActiveBookingStatus(booking.status));
    dispatch(displayActions.setActiveBookingTables([]));

    const statusOptions = StatusServiceInstance.getFilteredStatusList(
      statusName as any,
      booking as IBooking
    );
    dispatch(actions.setStatusOptions(statusOptions));
  }
);

const reloadAndCloseDialog =
  () =>
    async (dispatch: ThunkDispatch<DiaryRootState, {}, any>, getState: () => DiaryRootState) => {
      // dispatch(rootDisplayActions.setSlideState(SlidingStatesEnum.SlideOutComplete));
      // dispatch(bookingActions.updateBooking({
      //   _id: activeBooking._id,
      //   bookingType: activeBooking.bookingType,
      //   tables: activeBooking.tables,
      //   status: activeBooking.status
      // }))
      // dispatch(actions.resetStandby());

      dispatch(rootDisplayActions.setSlideState(SlidingStatesEnum.BCSlidingOut));
      setTimeout(() => {
        dispatch(rootDisplayActions.setSlideState(SlidingStatesEnum.SlideOutComplete));

        dispatch(validationActions.setSaveStatus(SaveStatus.Fresh));

        resetReducers(dispatch as AppDispatch);
        dispatch(displayActions.setMode(BookingMode.viewBooking));
      }, 500);
    };

const updateTables =
  () => (dispatch: ThunkDispatch<DiaryRootState, {}, any>, getState: () => DiaryRootState) => {
    const {
      displayReducer: { activeBooking: booking },
      activeSectionsReducer: { tablesListForActiveService },
      standbyReducer: { selectedTableIds },
    } = getState() as DiaryRootState;
    const selectedTablesFromList = TableSelectionService.getSelectedTablesFromList(
      tablesListForActiveService,
      selectedTableIds
    );

    // Update section if table is selected
    const sectionId = SectionsService.getSectionFromPrimaryTable(
      booking.primaryTableId,
      selectedTablesFromList
    );
    dispatch(
      displayActions.updateActiveBooking({
        tables: selectedTablesFromList,
        preferredSectionId: sectionId,
      })
    );
  };

export const handleCancelBooking =
  () => async (dispatch: ThunkDispatch<DiaryRootState, {}, any>) => {
    const {
      payload: { canSave },
    } = (await dispatch(updateCanSaveEditBooking())) as { payload: { canSave: boolean } };
    if (!canSave) {
      dispatch(validationActions.setSaveAttempted(true));
      return;
    }
    await dispatch(
      updateActiveBookingStatus({
        name: BookingStatusNames.Cancelled,
        isManualUpdate: true,
      })
    );

    // dispatch(update({ newStatusName: BookingStatusNames.Cancelled })).then((success) => {
    //   window.setTimeout(() => {
    //     dispatch(reloadAndCloseDialog());
    //   }, 300);
    // });

    const _saveStatus = await dispatch(
      saveEditBooking({ newStatusName: BookingStatusNames.Cancelled })
    );
    console.log(NS, '_saveStatus', _saveStatus);
    if (_saveStatus.payload === SaveStatus.SaveSuccessful) {
      dispatch(validationActions.setSaveStatus(SaveStatus.SaveSuccessfulAndClosing));
      setTimeout(() => dispatch(reloadAndCloseDialog()), 1000);
    }
  };

export const seat =
  (payload: { now: boolean; sms: boolean; seatNowFromViewMode?: boolean }) =>
    async (dispatch: ThunkDispatch<DiaryRootState, {}, any>, getState: () => DiaryRootState) => {
      const {
        payload: { canSave },
      } = (await dispatch(updateCanSaveEditBooking())) as { payload: { canSave: boolean } };
      if (!canSave) {
        dispatch(validationActions.setSaveAttempted(true));
        return;
      }
      const { now, sms, seatNowFromViewMode } = payload;
      const {
        standbyReducer: { selectedTableIds },
        displayReducer: { activeBooking },
      } = getState() as DiaryRootState;
      const tables =
        selectedTableIds && selectedTableIds.length ? selectedTableIds : activeBooking.tables;
      if (tables.length) {
        let newStatusName = null;
        if (now) {
          newStatusName = BookingStatusNames.Seated;
        }
        await dispatch(
          displayActions.updateActiveBooking({
            bookingType: BookingType.Booking,
          })
        );
        if (!seatNowFromViewMode) {
          dispatch(updateTables());
        }
        newStatusName = sms ? BookingStatusNames.SentSms : newStatusName;

        const _saveStatus = await dispatch(
          saveEditBooking({
            newStatusName,
            waitList: true,
            seatNowFromViewMode,
          })
        );
        console.log(NS, '_saveStatus', _saveStatus);
        if (_saveStatus.payload === SaveStatus.SaveSuccessful) {
          dispatch(validationActions.setSaveStatus(SaveStatus.SaveSuccessfulAndClosing));
          setTimeout(() => dispatch(reloadAndCloseDialog()), 1000);
        }
      }
    };

export const sendSMSToSBL =
  () =>
    async (dispatch: ThunkDispatch<DiaryRootState, {}, any>, getState: () => DiaryRootState) => {
      const { customerReducer, displayReducer, rootDisplayReducer } = getState() as DiaryRootState;
      const customer = customerReducer.customer;
      const activeBooking = displayReducer.activeBooking;
      const msg = rootDisplayReducer.toastPayload.msg;
      const phone = customer && customer.phone ? customer.phone : activeBooking.phone;
      smsService.send(cloneDeep(activeBooking), phone, msg);
    };

export const update =
  (payload: { newStatusName: string; waitList?: boolean; closeDialogOnSuccess?: boolean }) =>
    async (dispatch: ThunkDispatch<DiaryRootState, {}, any>, getState: () => DiaryRootState) => {
      const { newStatusName, waitList = false, closeDialogOnSuccess = true } = payload;

      /**
       * @todo: should be replaced with saving the active booking in edit mode - Jim working on it
       */

      if (closeDialogOnSuccess) {
        dispatch(
          actions.updateStandby({
            state: State.SUCCESS,
          })
        );
        setTimeout(() => {
          dispatch(displayActions.setSlideOut());
        }, 300);
      } else {
        dispatch(
          actions.updateStandby({
            state: State.ENTER,
          })
        );
      }
    };

const standbyListSlice = createSlice({
  name: 'standbyList', // used in action types
  initialState,
  reducers: {
    setLoading: (state: IStandbyListState, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setErrorMsg: (state: IStandbyListState, action: PayloadAction<string>) => {
      state.errorMsg = action.payload;
    },
    setStatusName: (
      state: IStandbyListState,
      action: PayloadAction<BookingStatusNames | string>
    ) => {
      state.statusName = action.payload;
    },
    setStatusOptions: (state: IStandbyListState, action: PayloadAction<IBookingStatus[]>) => {
      state.statusOptions = action.payload;
    },
    setIsSelectionValid: (state: IStandbyListState, action: PayloadAction<boolean>) => {
      state.isSelectionValid = action.payload;
    },
    updateStandby: (state: IStandbyListState, action: PayloadAction<any>) => {
      return { ...state, ...action.payload };
    },
    reset: () => {
      return initialState;
    },
  },
});

// Extract the action creators object and the reducer
const { actions, reducer } = standbyListSlice;

export { actions as standbyListActions };
// Export the reducer, either as a default or named export
export default reducer;
